import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Member } from '../lib/members';
import type { Boat, BoatPlace } from '../lib/boats';

type FilterValue =
  | 'all'
  | 'unreviewed'
  | 'rejected'
  | 'active'
  | 'former';

interface Props {
  members: (Member & { id: string })[];
  boats: (Boat & { id: string })[];
  places: BoatPlace[];
  initialFilter?: string;
}

interface MemberRow {
  member: Member & { id: string };
  approvalSummary: string;
  hasUnreviewed: boolean;
  hasRejected: boolean;
  placeNumbers: string[];
}

function buildRow(
  member: Member & { id: string },
  boats: (Boat & { id: string })[],
  places: BoatPlace[],
): MemberRow {
  const memberBoats = boats.filter((b) => b.ownerUid === member.id);
  const total = memberBoats.length;
  const approved = memberBoats.filter((b) => b.approval?.approved === true)
    .length;
  const hasUnreviewed = memberBoats.some((b) => b.approval == null);
  const hasRejected = memberBoats.some(
    (b) => b.approval?.approved === false,
  );
  const summary = total === 0 ? '—' : `${approved}/${total}`;
  const placeNumbers = places
    .filter((p) => p.currentOwnerUid === member.id)
    .map((p) => p.number)
    .sort();
  return {
    member,
    approvalSummary: summary,
    hasUnreviewed,
    hasRejected,
    placeNumbers,
  };
}

function matchesSearch(row: MemberRow, q: string): boolean {
  if (!q.trim()) return true;
  const needle = q.toLowerCase().trim();
  const hay = [
    row.member.firstName,
    row.member.lastName,
    row.member.email,
    ...row.placeNumbers,
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

function matchesFilter(row: MemberRow, filter: FilterValue): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'unreviewed':
      return row.hasUnreviewed;
    case 'rejected':
      return row.hasRejected;
    case 'active':
      return row.member.status === 'active';
    case 'former':
      return row.member.status === 'former';
  }
}

export default function AdminMemberList({
  members,
  boats,
  places,
  initialFilter,
}: Props) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterValue>(
    (initialFilter as FilterValue) ?? 'all',
  );

  const rows = useMemo(
    () => members.map((m) => buildRow(m, boats, places)),
    [members, boats, places],
  );
  const visible = rows.filter(
    (r) => matchesFilter(r, filter) && matchesSearch(r, search),
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Medlemmar ({members.length})</h1>
        <Link
          to="/admin/medlemmar/ny"
          className="px-3 py-2 bg-navy text-white rounded text-sm"
        >
          + Ny medlem
        </Link>
      </div>
      <div className="flex flex-col md:flex-row gap-2">
        <input
          type="search"
          placeholder="🔍 Sök namn/email/plats"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded border-gray-300 px-3 py-2"
        />
        <label className="flex items-center gap-2">
          <span className="text-sm">Visa:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterValue)}
            className="rounded border-gray-300 px-2 py-2"
          >
            <option value="all">Alla</option>
            <option value="unreviewed">Med ej-granskade båtar</option>
            <option value="rejected">Med avslagna båtar</option>
            <option value="active">Aktiva</option>
            <option value="former">Inaktiva</option>
          </select>
        </label>
      </div>
      <ul className="space-y-2">
        {visible.map((row) => (
          <li key={row.member.id}>
            <Link
              to={`/admin/medlemmar/${row.member.id}`}
              className="block border rounded p-3 hover:bg-gray-50"
            >
              <div className="flex justify-between items-baseline">
                <span className="font-medium">
                  {row.member.firstName} {row.member.lastName}
                </span>
                <span className="text-sm text-gray-600">
                  {row.member.email}
                </span>
              </div>
              <div className="flex justify-between text-sm text-gray-700 mt-1">
                <span>
                  {row.placeNumbers.length > 0
                    ? `Plats ${row.placeNumbers.join(', ')}`
                    : '(ingen plats)'}
                </span>
                <span>Approval: {row.approvalSummary}</span>
              </div>
              <div className="flex gap-2 text-xs mt-1">
                <span
                  className={
                    row.member.status === 'active'
                      ? 'text-green-700'
                      : 'text-gray-500'
                  }
                >
                  {row.member.status === 'active' ? '✓ Aktiv' : 'Inaktiv'}
                </span>
                {row.member.role === 'board' && (
                  <span className="text-blue-700">✓ Styrelse</span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
